// src/services/signalementService.ts
import { BACKEND_CORE_URL } from '@/config/api'

// Utiliser la configuration centralisée depuis les variables d'environnement
const API_BASE_URL = BACKEND_CORE_URL



export interface Signalement {
  id?: number
  id_signalement: string
  code_suivi: string
  statut: string
  type_signalement: string
  osc?: {
    id?: number
    identifiant: string
    nom: string
  }
  soumission?: {
    date?: string
    canal?: string
    documents?: string[]
  }
  description_faits?: {
    description?: string
    lieu?: string
    date_faits?: string
    temoins?: string[]
    comment_connaissance?: string
    signalement_autre_organisme?: string
  }
  organisme_mis_en_cause?: {
    nom?: string
    type?: string
    adresse?: string
    contact?: {
      email?: string
      telephone?: string
    }
  }
  messagerie?: {
    thread_key?: string
    derniere_activite?: string
  }
  metadonnees?: {
    tags?: string[]
  }
  analyse_referents?: {
    historique_statuts?: Array<{
      ancien_statut?: string
      nouveau_statut: string
      user_id?: number
      date: string
      commentaire?: string
    }>
    referents_assignes?: number[]
  }
  created_at: string
  updated_at?: string
}

export interface ApiError {
  message: string
  status?: number
}

/**
 * Récupère un signalement par son code de suivi (route publique)
 * Route: GET /api/public/signalements/{code_suivi}
 * 
 * @param codeSuivi - Le code de suivi du signalement (ex: "7CE5-A442-EE0B")
 * @returns Promise<Signalement> - Les données du signalement
 * @throws ApiError - Si le signalement n'est pas trouvé ou si une erreur survient
 */
export async function getSignalementByCode(
  codeSuivi: string
): Promise<Signalement> {
  // Nettoyer le code (enlever les espaces, convertir en majuscules)
  const cleanCode = codeSuivi.trim().toUpperCase().replace(/\s+/g, '')
  
  // Construire l'URL avec la route publique exacte
  const url = `${API_BASE_URL}/public/signalements/${encodeURIComponent(cleanCode)}`
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  })

  if (!response.ok) {
    let errorMessage = 'Erreur lors de la récupération du signalement'
    
    try {
      const errorData = await response.json()
      errorMessage = errorData.message || errorMessage
    } catch {
      // Si la réponse n'est pas du JSON
      errorMessage = `Erreur ${response.status}: ${response.statusText}`
    }

    const error: ApiError = {
      message: errorMessage,
      status: response.status,
    }
    throw error
  }

  const responseData = await response.json()
  
  // Extraire les données si elles sont encapsulées dans un objet "data"
  if (responseData.data) {
    return responseData.data as Signalement
  }
  
  // Sinon, retourner directement les données
  return responseData as Signalement
}

